import React, { useState, useEffect } from "react";
import "@/styles/rich-prose.css";
import SEOMeta from "@/components/SEOMeta";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { BlogPost } from "@/entities/BlogPost";
import { generateSlug, getBlogUrl } from "@/lib/detailSlugs";
import { 
  Calendar, 
  User, 
  Tag,
  ArrowLeft,
  Clock,
  Share2,
  Facebook,
  Twitter,
  Linkedin
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import Breadcrumbs from "@/components/Breadcrumbs";
import RelatedCourses from "@/components/blog/RelatedCourses";

export default function BlogDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [comment, setComment] = useState({ name: "", email: "", message: "" });
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      // New slug-based URL: /blog/:slug — fetch by slug
      loadPostBySlug(slug);
    } else {
      // Legacy ?id= URL: /BlogDetail?id=xxx — fetch by ID, then redirect to slug URL
      const params = new URLSearchParams(location.search);
      const postId = params.get('id');
      if (postId) {
        loadPostAndRedirect(postId);
      } else {
        setLoading(false);
      }
    }
  }, [slug, location.search]);

  // Set only when a lookup definitively returned nothing (never on a fetch error)
  const [isMissing, setIsMissing] = useState(false);

  const loadPostBySlug = async (slugValue) => {
    try {
      // 1. Try matching by stored slug
      const results = await BlogPost.filter({ slug: slugValue, is_published: true });
      if (results.length > 0) {
        setPost(results[0]);
        loadRelated(results[0]);
        return;
      }

      // 2. Fallback: maybe the slug is actually an old ID (pre-migration)
      try {
        const byId = await BlogPost.get(slugValue);
        if (byId && byId.is_published) {
          setPost(byId);
          loadRelated(byId);
          return;
        }
      } catch {}

      // 3. Fallback: posts without a stored slug — match by generated slug from title
      const allPosts = await BlogPost.filter({ is_published: true });
      const match = allPosts.find(p => generateSlug(p.title) === slugValue);
      if (match) {
        setPost(match);
        loadRelated(match);
      } else {
        setIsMissing(true);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading post:', error);
      setLoading(false);
    }
  };

  const loadPostAndRedirect = async (postId) => {
    try {
      const currentPost = await BlogPost.get(postId);
      if (currentPost) {
        const slugValue = currentPost.slug || generateSlug(currentPost.title);
        if (slugValue) {
          navigate(`/blog/${slugValue}`, { replace: true });
          return;
        }
        // No slug — just show the post at the old URL
        setPost(currentPost);
        loadRelated(currentPost);
      } else {
        setIsMissing(true);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading post:', error);
      setLoading(false);
    }
  };

  const loadRelated = async (currentPost) => {
    try {
      const related = await BlogPost.filter({
        category: currentPost.category,
        is_published: true
      }, '-created_date', 4);
      setRelatedPosts(related.filter(p => p.id !== currentPost.id).slice(0, 3));
    } catch (error) {
      console.error('Error loading related:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCommentSubmit = (e) => {
    e.preventDefault();
    const newComment = {
      ...comment,
      date: new Date().toISOString(),
      id: Date.now().toString()
    };
    setComments([...comments, newComment]);
    setComment({ name: "", email: "", message: "" });
  };

  const getCategoryColor = (category) => {
    const colors = {
      first_aid_tips: "bg-red-100 text-red-800",
      safety_advice: "bg-blue-100 text-blue-800",
      training_updates: "bg-green-100 text-green-800",
      health_news: "bg-purple-100 text-purple-800",
      workplace_safety: "bg-orange-100 text-orange-800"
    };
    return colors[category] || "bg-gray-100 text-gray-800";
  };

  const getCategoryName = (category) => {
    const names = {
      first_aid_tips: "First Aid Tips",
      safety_advice: "Safety Advice",
      training_updates: "Training Updates",
      health_news: "Health News",
      workplace_safety: "Workplace Safety"
    };
    return names[category] || category;
  };

  const shareUrl = window.location.href;
  const canonicalUrl = post ? `https://livius-training.co.uk${getBlogUrl(post)}` : undefined;

  // Inject Article structured data
  useEffect(() => {
    if (!post) return;
    const existing = document.getElementById("structured-data-article");
    if (existing) existing.remove();
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": post.title,
      "description": post.excerpt || post.title,
      "image": post.featured_image || "https://livius-training.co.uk/og-image.jpg",
      "datePublished": post.created_date,
      "dateModified": post.updated_date || post.created_date,
      "author": { "@type": "Person", "name": post.author || "Livius Training" },
      "publisher": {
        "@type": "Organization",
        "name": "Livius Training",
        "url": "https://livius-training.co.uk",
        "logo": { "@type": "ImageObject", "url": "https://livius-training.co.uk/favicon.png" }
      },
      "mainEntityOfPage": { "@type": "WebPage", "@id": canonicalUrl }
    };
    const script = document.createElement("script");
    script.id = "structured-data-article";
    script.type = "application/ld+json";
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById("structured-data-article")?.remove(); };
  }, [post, canonicalUrl]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">
        <SEOMeta noindex notFound={isMissing} title="Article Not Found" />
        <h1 className="text-2xl font-bold text-gray-900 mb-3">Article Not Found</h1>
        <p className="text-gray-500 mb-6">This article may have been moved or is no longer available.</p>
        <Link to="/blog" className="text-brand font-semibold hover:underline">
          ← Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOMeta
        title={post.seo?.meta_title || post.title}
        description={post.seo?.meta_description || post.excerpt}
        image={post.featured_image}
        canonical={canonicalUrl}
      />
      {/* Header */}
      <section className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumbs
            className="mb-6"
            items={[
              { name: "Home", url: "/" },
              { name: "Blog", url: "/blog" },
              { name: post.title, url: getBlogUrl(post) }
            ]}
          />
          
          <div className="mb-4">
            <Badge className={getCategoryColor(post.category)}>
              {getCategoryName(post.category)}
            </Badge>
          </div>
          
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            {post.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center">
              <User className="w-4 h-4 mr-2" />
              {post.author}
            </div>
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              {new Date(post.created_date).toLocaleDateString('en-GB', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
              })}
            </div>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              {Math.ceil(post.content.split(' ').length / 200)} min read
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      {post.featured_image && (
        <section className="bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <img 
              src={post.featured_image} 
              alt={post.image_alt || `${post.title} - Livius Training`}
              width={896} height={384}
              loading="lazy"
              className="w-full h-96 object-cover rounded-lg shadow-lg"
            />
          </div>
        </section>
      )}

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <div 
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-8 pt-8 border-t">
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, index) => (
                    <Badge key={index} variant="outline" className="text-sm">
                      <Tag className="w-3 h-3 mr-1" />
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Share */}
            <div className="mt-8 pt-8 border-t">
              <div className="flex items-center gap-4">
                <span className="text-sm font-semibold text-gray-700 flex items-center">
                  <Share2 className="w-4 h-4 mr-2" />
                  Share this article:
                </span>
                <a 
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a 
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-500"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <a 
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-700 hover:text-blue-800"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>

          {/* Related Courses — contextual internal linking */}
          <RelatedCourses blogCategory={post.category} />

          {/* Course CTA Block */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8 border-l-4 border-brand">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Looking for training?</h3>
            <p className="text-gray-600 mb-4 text-sm">
              We deliver accredited {getCategoryName(post.category).toLowerCase()} courses across Yorkshire — in our classroom, at your workplace, or online.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/open-courses">
                <Button className="bg-brand hover:bg-brand-700 text-white text-sm">Browse Courses</Button>
              </Link>
              <Link to="/onsite-training">
                <Button variant="outline" className="border-brand text-brand hover:bg-brand hover:text-white text-sm">On-Site Quote</Button>
              </Link>
              <Link to="/contact">
                <Button variant="ghost" className="text-sm">Contact Us</Button>
              </Link>
            </div>
          </div>

          {/* Comments Section */}
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Comments ({comments.length})
            </h2>

            {/* Comment Form */}
            <Card className="mb-8">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4">Leave a Comment</h3>
                <form onSubmit={handleCommentSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        value={comment.name}
                        onChange={(e) => setComment({...comment, name: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={comment.email}
                        onChange={(e) => setComment({...comment, email: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message">Comment *</Label>
                    <Textarea
                      id="message"
                      value={comment.message}
                      onChange={(e) => setComment({...comment, message: e.target.value})}
                      required
                      rows={4}
                    />
                  </div>
                  <Button type="submit" className="bg-brand hover:bg-brand-700">
                    Post Comment
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Comments List */}
            <div className="space-y-6">
              {comments.map((c) => (
                <div key={c.id} className="border-b pb-6 last:border-b-0">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-brand font-semibold text-lg">
                        {c.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-gray-900">{c.name}</span>
                        <span className="text-sm text-gray-500">
                          {new Date(c.date).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-gray-700">{c.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Related Posts */}
          {relatedPosts.length > 0 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Articles</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {relatedPosts.map((relatedPost) => (
                  <Link key={relatedPost.id} to={getBlogUrl(relatedPost)}>
                    <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 group">
                      <div className="relative overflow-hidden">
                        <img 
                          src={relatedPost.featured_image || "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=400&h=250&fit=crop"} 
                          alt={relatedPost.image_alt || relatedPost.title}
                          width={400} height={128} loading="lazy" decoding="async"
                          className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 text-sm">
                          {relatedPost.title}
                        </h3>
                        <p className="text-gray-600 text-xs line-clamp-2">
                          {relatedPost.excerpt}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}